/**
 * Cameroon IBAN (CM) validation per ISO 13616.
 *
 * Format: CM + 2 check digits + 23 alphanumeric → 27 chars total.
 * Validation:
 *   1. Strip spaces, uppercase.
 *   2. Length must be 27.
 *   3. Must start with `CM`.
 *   4. Move first 4 chars to the end, replace letters A=10..Z=35, mod 97 == 1.
 */

const COUNTRY = 'CM';
const LENGTH = 27;

function letterToNum(ch: string): string {
  const code = ch.charCodeAt(0);
  if (code >= 48 && code <= 57) return ch; // 0-9
  if (code >= 65 && code <= 90) return String(code - 55); // A=10..Z=35
  return '?';
}

function mod97(numericString: string): number {
  let remainder = 0;
  for (const digit of numericString) {
    remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder;
}

export function normalizeIban(input: string): string {
  return input.replace(/\s+/g, '').toUpperCase();
}

export function isValidCameroonIban(input: string): boolean {
  if (typeof input !== 'string') return false;
  const iban = normalizeIban(input);
  if (iban.length !== LENGTH) return false;
  if (!iban.startsWith(COUNTRY)) return false;
  if (!/^[A-Z0-9]+$/.test(iban)) return false;

  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let numeric = '';
  for (const ch of rearranged) {
    const mapped = letterToNum(ch);
    if (mapped === '?') return false;
    numeric += mapped;
  }
  return mod97(numeric) === 1;
}

/** Format an IBAN in 4-character groups for display. Does not validate. */
export function formatIban(input: string): string {
  const iban = normalizeIban(input);
  return iban.match(/.{1,4}/g)?.join(' ') ?? iban;
}

/**
 * Compute the 2-digit ISO 13616 check value for a given country + BBAN.
 * Useful for constructing valid IBANs in tests and seed data.
 */
export function computeIbanCheckDigits(countryCode: string, bban: string): string {
  const cc = countryCode.toUpperCase();
  const rearranged = bban.toUpperCase() + cc + '00';
  let numeric = '';
  for (const ch of rearranged) {
    const mapped = letterToNum(ch);
    if (mapped === '?') throw new Error(`Invalid IBAN character: ${ch}`);
    numeric += mapped;
  }
  const check = 98 - mod97(numeric);
  return String(check).padStart(2, '0');
}
